import { z } from "zod";
import type { SectionDef } from "./_types";
import { agree } from "./nounGrammar";

// "Novo"/"Nova" lives in the grammar module; re-exported for existing callers.
export { newPrefix } from "./nounGrammar";

/**
 * KLUB-283 — a vocabulary term is words only: letters (accented included),
 * spaces, hyphen and apostrophe, with at least one letter. Tenant staff write
 * these terms and the server places them into push and e-mail text, so markup
 * characters (`<`, `>`, `&`, `;`, quotes) are refused at the edge too. Mirrored
 * byte for byte in supabase/functions/write-tenant-setting/validators.ts
 * (tests/shared/tenant-settings/vocabulary-parity.test.ts).
 */
export const VOCAB_TERM_PATTERN = /^(?=.*\p{L})[\p{L}\p{M} '’-]+$/u;
export const VOCAB_TERM_MESSAGE = "Use apenas letras, espaços, hífen e apóstrofo.";
const VocabTerm = z.string().min(1).max(50).regex(VOCAB_TERM_PATTERN, VOCAB_TERM_MESSAGE);

const NameTriple = z.object({
  singular: VocabTerm,
  plural:   VocabTerm,
  cta_add:  VocabTerm,
  gender:   z.enum(["m", "f"]).default("m"),
});

export type GenderedNoun = z.infer<typeof NameTriple>;

/**
 * Home title for the upcoming-activities section.
 * Agrees with the noun's gender. A missing or invalid gender reads as the
 * default activity noun's gender (KLUB-283), not masculine.
 *
 *   upcomingActivitiesTitle({ plural: "Experiências", gender: "f" }) → "Próximas Experiências"
 *   upcomingActivitiesTitle({ plural: "Eventos", gender: "m" })      → "Próximos Eventos"
 *
 * `gender` is `string` on purpose. Generated `settings.ts` is `as const`, so
 * a tenant whose activity is masculine would make `=== "f"` unreachable if
 * the parameter kept that literal.
 */
export function upcomingActivitiesTitle(noun: { plural: string; gender?: string | null }): string {
  const determiner = agree(noun, "Próximos", "Próximas", vocabulary.defaults.activity.gender);
  return `${determiner} ${noun.plural}`;
}

const VocabShape = {
  member:     NameTriple,
  prospect:   NameTriple,
  activity:   NameTriple,
  space:      NameTriple,
  booking:    NameTriple,
  benefit:    NameTriple,
  // The home feed section, the feed screen and a titleless post. The section
  // heading is the plural. A stored home title still overrides that heading.
  feed:       NameTriple,
  tier:       NameTriple,
  interest:   NameTriple,
  referral:   NameTriple,
  invite:     NameTriple,
  staff:      NameTriple,
  // Community v1 — plan U11. Four new nouns covering the community surface
  // (directory + groups), seasonal challenges, and the awarded badge.
  community:  NameTriple,
  group:      NameTriple,
  challenge:  NameTriple,
  // Missions & Journey — a mission is an ordered sequence of challenges that
  // forms the member's permanent Journey (white-label noun).
  mission:    NameTriple,
  badge:      NameTriple,
  // People list — plan #185 U6. Covers member and prospect at once, so neither
  // existing noun can title the screen. cta_add is required by NameTriple but
  // unused: nobody is created from this screen — a person arrives by signing
  // up, filling the interest form, or being invited, and becomes a member by
  // paying, never by an operator typing them in.
  person:     NameTriple,
  section: z.object({
    content: VocabTerm,
    club:    VocabTerm,
  }),
};

export const vocabulary = {
  schema: z.object(VocabShape),
  defaults: {
    member:     { singular: "Membro",      plural: "Membros",       cta_add: "Novo membro",     gender: "m" },
    // KLUB-283 R6/R7: prospect and tier defaults follow the copy the app
    // already showed ("Não membro", "Plano"), so a club with no vocabulary
    // sees no change when the fixed words become vocabulary reads.
    prospect:   { singular: "Não membro",  plural: "Não membros",   cta_add: "Novo não membro", gender: "m" },
    // Origin Key Decisions §R22: default plural is "Experiências" (not
    // "Atividades"); tenants override via vocabulary settings if they want
    // a different surface (e.g. "Eventos", "Programações").
    activity:   { singular: "Experiência", plural: "Experiências",  cta_add: "Nova experiência", gender: "f" },
    space:      { singular: "Espaço",      plural: "Espaços",       cta_add: "Novo espaço",     gender: "m" },
    booking:    { singular: "Reserva",     plural: "Reservas",      cta_add: "Nova reserva",    gender: "f" },
    benefit:    { singular: "Benefício",   plural: "Benefícios",    cta_add: "Novo benefício",  gender: "m" },
    feed:       { singular: "Novidade",    plural: "Novidades",     cta_add: "Nova novidade",   gender: "f" },
    tier:       { singular: "Plano",       plural: "Planos",        cta_add: "Novo plano",      gender: "m" },
    interest:   { singular: "Interesse",   plural: "Interesses",    cta_add: "Novo interesse",  gender: "m" },
    referral:   { singular: "Indicação",   plural: "Indicações",    cta_add: "Nova indicação",  gender: "f" },
    invite:     { singular: "Convite",     plural: "Convites",      cta_add: "Novo convite",    gender: "m" },
    staff:      { singular: "Staff",       plural: "Staff",         cta_add: "Novo staff",      gender: "m" },
    community:  { singular: "Comunidade",  plural: "Comunidades",   cta_add: "Nova comunidade", gender: "f" },
    group:      { singular: "Grupo",       plural: "Grupos",        cta_add: "Novo grupo",      gender: "m" },
    challenge:  { singular: "Desafio",     plural: "Desafios",      cta_add: "Novo desafio",    gender: "m" },
    mission:    { singular: "Trilha",      plural: "Trilhas",       cta_add: "Nova trilha",     gender: "f" },
    badge:      { singular: "Conquista",   plural: "Conquistas",    cta_add: "Nova conquista",  gender: "f" },
    person:     { singular: "Pessoa",      plural: "Pessoas",       cta_add: "Nova pessoa",     gender: "f" },
    section:    { content: "Conteúdo",     club: "Clube" },
  },
  meta: {
    member:     { label: "Member",     group: "nouns",    widget: "text" },
    prospect:   { label: "Prospect",   group: "nouns",    widget: "text" },
    activity:   { label: "Activity",   group: "nouns",    widget: "text" },
    space:      { label: "Space",      group: "nouns",    widget: "text" },
    booking:    { label: "Booking",    group: "nouns",    widget: "text" },
    benefit:    { label: "Benefit",    group: "nouns",    widget: "text" },
    feed:       { label: "Feed",       group: "nouns",    widget: "text" },
    tier:       { label: "Tier",       group: "nouns",    widget: "text" },
    interest:   { label: "Interest",   group: "nouns",    widget: "text" },
    referral:   { label: "Referral",   group: "nouns",    widget: "text" },
    invite:     { label: "Invite",     group: "nouns",    widget: "text" },
    staff:      { label: "Staff",      group: "nouns",    widget: "text" },
    community:  { label: "Community",  group: "nouns",    widget: "text" },
    group:      { label: "Group",      group: "nouns",    widget: "text" },
    challenge:  { label: "Challenge",  group: "nouns",    widget: "text" },
    mission:    { label: "Mission",    group: "nouns",    widget: "text" },
    badge:      { label: "Badge",      group: "nouns",    widget: "text" },
    person:     { label: "Person",     group: "nouns",    widget: "text" },
    section:    { label: "Section labels", group: "sections", widget: "text" },
  },
  forbiddenLiterals: [],
} as const satisfies SectionDef<typeof VocabShape>;

export type VocabValues = z.infer<typeof vocabulary.schema>;

export function flattenVocab(nested: VocabValues): Record<string, string> {
  const flat: Record<string, string> = {};
  for (const [topKey, topValue] of Object.entries(nested)) {
    for (const [leafKey, leafValue] of Object.entries(topValue as Record<string, string>)) {
      flat[`${topKey}.${leafKey}`] = String(leafValue);
    }
  }
  return flat;
}

export function unflattenVocab(flat: Record<string, string>): VocabValues {
  const nested: Record<string, Record<string, string>> = {};
  for (const [key, value] of Object.entries(flat)) {
    const [top, leaf] = key.split(".", 2);
    if (!nested[top]) nested[top] = {};
    nested[top][leaf] = value;
  }
  return nested as VocabValues;
}
